export type JsonSchema = Readonly<Record<string, unknown>>;
export type AgentToolInput = Readonly<Record<string, unknown>>;
export type AgentToolScope = 'app' | 'screen' | 'overlay';

export interface AgentToolResult {
  readonly content: readonly { readonly type: 'text'; readonly text: string }[];
  readonly isError?: boolean;
  /** O retorno do `run` como objeto (o `structuredContent` do MCP); o `content` leva o mesmo em JSON. */
  readonly structuredContent?: Readonly<Record<string, unknown>>;
}

export interface AgentToolPreview {
  readonly title: string;
  readonly detail?: string;
  readonly tone?: 'default' | 'destructive';
}

export interface AgentTool {
  readonly name: string;
  readonly description: string;
  readonly inputSchema: JsonSchema;
  readonly readOnly: boolean;
  readonly scope: AgentToolScope;
  readonly origin: { readonly screenId?: string; readonly actionId?: string };
  /** Só para o Luy, pelo adaptador em memória; nunca vai ao `navigator.modelContext` (U46, D5). */
  readonly internal?: true;
  /** Lido a cada turno do Luy e somado à descrição declarada: a tela que a pessoa vê agora (U56). O WebMCP não usa. */
  readonly turnNote?: () => string;
  readonly preview?: (input: AgentToolInput) => AgentToolPreview | Promise<AgentToolPreview>;
  /** `signal`: quem chama desistiu (prazo ou cancelamento); a ferramenta não começa efeito depois dele. */
  execute(input: AgentToolInput, options?: { readonly signal?: AbortSignal }): Promise<AgentToolResult>;
}

export const EMPTY_INPUT_SCHEMA: JsonSchema = Object.freeze({ type: 'object', properties: {}, additionalProperties: false });

export function toolText(text: string): AgentToolResult {
  return { content: [{ type: 'text', text }] };
}

export function toolFailure(text: string): AgentToolResult {
  return { content: [{ type: 'text', text }], isError: true };
}

/** Valor que vira JSON sem lançar (ciclo, `toJSON` que lança, `BigInt`); `undefined` quando não vira. */
export function jsonSafe(value: unknown): unknown {
  if (value === undefined || typeof value === 'function' || typeof value === 'symbol') return undefined;
  try {
    return JSON.parse(JSON.stringify(value)) as unknown;
  } catch {
    return undefined;
  }
}

/** Resultado com dado: texto JSON para quem só lê `content` e o objeto em `structuredContent`. */
export function toolData(value: unknown): AgentToolResult {
  const safe = jsonSafe(value);
  const structured = typeof safe === 'object' && safe !== null && !Array.isArray(safe) ? (safe as Record<string, unknown>) : { result: safe ?? null };
  return { content: [{ type: 'text', text: JSON.stringify(structured) }], structuredContent: structured };
}
